import { useContext } from 'react'

import { formatDate, launchModal } from '@domain-group/javascript-utils'
import { useLatestEntity } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import { baseURL } from '@functions/api'
import { destructiveActions } from '@functions/activity'
import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ActivityModel, ActivityRequestOptions } from '@models/types'

import * as activityActions from '@redux/modules/activity'
import type { AppDispatch } from '@redux/store'
import type { ModuleState } from '@redux/modules/types'

const stringifyObjectKeys: (keyof ActivityModel)[] = ['parameters']

export type GenerateUrlsOptions = {
  prefixRoute: string,
}

export const generateUrls = (
  activity?: Partial<ActivityModel>,
  options?: GenerateUrlsOptions,
) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    activitiesIndexUrl: `#/${prefixRoute}/activity`,
    // @ts-expect-error TS18048
    exportActivity: `${baseURL}/activities/${activity.id}/export.json`,
  }
}

type CreateActivityParams = {
  activityParams: Partial<ActivityModel>,
  dispatch: AppDispatch,
  requestOptions?: ActivityRequestOptions,
}

const createActivity = (params: CreateActivityParams) => {
  const { dispatch, activityParams, requestOptions } = params
  const { createActivity: createFn } = activityActions

  const updatedParams = mergeAndStringifyEntityObjects({}, activityParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

// Return related entities
export const useRelations = (entity: Partial<ActivityModel> = {}) => {
  const { owner_id, owner_type } = entity

  const { users } = useSelector(reduxState => reduxState.entities)

  const user = owner_type === 'User' && owner_id && users[owner_id] ? users[owner_id] : {}

  return {
    user,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ActivityRequestOptions,
}

function useActivity(initEntity: Partial<ActivityModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: activity }: { entity: ActivityModel} = useLatestEntity(initEntity, 'activities')
  const { id } = activity

  const dispatch = useDispatch()

  useReduxAction(
    'activities',
    'loadActivity',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const { callbacks } = useContext(PageContext)

  const { user } = useRelations(activity)

  const { creating, loading, loadedIds } = useSelector(reduxState => reduxState.activities)

  const {
    action, parameters, recipient_id, recipient_type, trackable, trackable_id, trackable_type,
  } = activity

  const { changes = {} } = parameters || {}

  const createdAt = activity?.dates?.created?.date_time || ''
  const formattedDateLong = formatDate(createdAt, 'dd/MM/yyyy HH:mm', "ccc d LLL yyyy 'at' ta")
  const formattedDateShort = formatDate(createdAt, 'dd/MM/yyyy HH:mm', "d/MM/yy 'at' h:mma")

  const recipientIsTrackable = recipient_id === trackable_id && recipient_type === trackable_type
  const activityHasReason = !!trackable?.reason

  return {
    activity,
    activityHasReason,
    callbacks: {
      createActivity: (activityParams: Partial<ActivityModel>) => (
        createActivity({ activityParams, dispatch })
      ),
      manageActivity: () => launchModal({
        callbacks,
        modalKey: 'ManageActivityModal',
        payload: { activity },
      }),
    },
    creating,
    formattedDateLong,
    formattedDateShort,
    hasChanges: !!Object.keys(changes).length,
    isDestructive: destructiveActions.some(item => action?.includes(item)) || parameters?.deleted,
    loading: loading && !loadedIds.includes(activity.id),
    recipientIsTrackable,
    urls: generateUrls(activity),
    user,
  }
}

export default useActivity
